import { ChangeEvent, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import Dropdown from '@/components/Dropdown/Dropdown';
import Modal from '@/components/Modal/Modal';
import RequestProcessingModal from '@/components/Modal/RequestProcessingModal/RequestProcessingModal';
import { useUpdateGroup } from '@/services/groups/groups.service';
import { useRequestProcessingModal } from '@/hooks/useRequestProcessingModal';
import styles from './MoveStores.module.css';
import { MoveStoresModalProps } from './MoveStoresModal.types';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const MoveStoresModal: FC<MoveStoresModalProps> = ({
  isOpen,
  onClose,
  selectedStores,
  currentGroup,
  currentGroupId,
  allGroups,
  onSuccess,
  isDeleteGroup,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const [selectedGroup, setSelectedGroup] = useState<string>('');

  const { isModalOpen, modalType, showTimeoutModal, showErrorModal, handleCloseAndRefresh } = useRequestProcessingModal(
    { onRefresh },
  );

  const { mutate, isLoading } = useUpdateGroup(selectedGroup);

  const handleConfirm = () => {
    mutate(
      {
        name: allGroups.find((group) => group.id === selectedGroup)?.name,
        stores: selectedStores.map((store) => store.storeNo),
        groupIdToDelete: currentGroupId,
      },
      {
        onSuccess,
        onError: (error) => {
          if (error instanceof Error && error.message === 'API Gateway request timed out') {
            onClose();
            showTimeoutModal();
            return;
          }
          onClose();
          showErrorModal();
        },
      },
    );
  };

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedGroup(event.target.value);
  };

  const storesSelected = selectedStores.map((selectedStore) => selectedStore.storeNo).join(', ');

  return (
    <>
      <Modal
        className={styles.moveStores}
        isOpen={isOpen}
        onClose={onClose}
        headerContent={isDeleteGroup ? t('moveStoresModal.titleMove') : t('moveStoresModal.title')}
        data-testid="Modal"
        showTitleDivider
        bodyContent={
          <div className={styles.moveStoresBody}>
            <div className={styles.newGroupSelection}>
              <div className={styles.currentGroupTitle}>Stores Selected:</div>
              <div className={styles.storeFont}>{storesSelected}</div>
              <div className={styles.storeFont}>{`(${currentGroup})`}</div>
              <ArrowDownwardIcon sx={{ fontSize: '20px' }} />
              <div className={styles.newGroupDropdown}>
                <Dropdown
                  data-testid="Dropdown"
                  id="selectNewStoreGroup"
                  label={t('moveStoresModal.newStoreGroupLabel')}
                  options={allGroups
                    .filter((group) => group.name !== currentGroup)
                    .map((group) => ({ label: group.name, value: group.id }))}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        }
        footerContent={
          <div className={styles.footer}>
            <div className={styles.footerMessage}>
              <p>
                {t('common.submissionPendingRequestDisclaimer')}
                <span>{t('common.calendar.menuDownloadTimeline')}</span>
              </p>
            </div>
            <div className={styles.footerSubMessage}>
              <p>{t('common.checkCalendar')}</p>
            </div>
            <div className={styles.buttons}>
              <Button variant={ButtonVariants.Secondary} onClick={onClose} type="button">
                {t('common.cancel')}
              </Button>
              <Button onClick={handleConfirm} type="button" disabled={selectedGroup === ''} isLoading={isLoading}>
                {isDeleteGroup ? t('moveStoresModal.confirmButton') : t('common.confirm')}
              </Button>
            </div>
          </div>
        }
      />
      {modalType && <RequestProcessingModal isOpen={isModalOpen} onClose={handleCloseAndRefresh} type={modalType} />}
    </>
  );
};

export default MoveStoresModal;
